import htsSnapshot from '@/features/case-workspace/data/usitc-hts-rev7.json'

export type HtsRegulatoryFlag =
  | 'SECTION_232_STEEL'
  | 'SECTION_232_ALUMINUM'
  | 'AD_CVD_POSSIBLE'
  | 'QUOTA_OR_VISA'
  | 'IRC_TAX_APPLICABLE'
  | 'SPECIAL_PROGRAM_INDICATOR_ALLOWED'

export type UsitcHtsDatasetManifest = {
  sourceName: string
  revisionLabel: string
  releaseDate: string
  archiveUrl: string
  jsonDownloadUrl: string
}

export type NormalizedHtsRecord = {
  htsCode: string
  rawHtsCode?: string
  superior?: string | null
  indent?: string | null
  description: string
  units?: string[]
  generalRate?: string
  specialRate?: string
  otherRate?: string
  quotaQuantity?: string | null
  footnotesText?: string[]
  additionalDuties?: string
  flags: HtsRegulatoryFlag[]
  notes?: string[]
}

type HtsSnapshotPayload = {
  manifest: UsitcHtsDatasetManifest
  records: NormalizedHtsRecord[]
}

const SNAPSHOT_PAYLOAD = htsSnapshot as HtsSnapshotPayload

const BUILTIN_REGULATORY_OVERLAYS: NormalizedHtsRecord[] = [
  {
    htsCode: '7606.12.3091',
    description: 'Aluminum plates, sheets and strip, rectangular, alloyed',
    units: ['kg'],
    generalRate: '5.7%',
    specialRate: 'Free (A+,AU,BH,CL,CO,D,E,IL,JO,KR,MA,OM,P,PA,PE,S,SG)',
    flags: ['SECTION_232_ALUMINUM', 'SPECIAL_PROGRAM_INDICATOR_ALLOWED'],
    notes: ['Current built-in overlay marks this code for Section 232 aluminum review.'],
  },
  {
    htsCode: '7219.34.0000',
    description: 'Flat-rolled products of stainless steel, width 600 mm or more, cold-rolled',
    units: ['kg'],
    generalRate: 'Free',
    flags: ['SECTION_232_STEEL', 'AD_CVD_POSSIBLE'],
    notes: ['Current built-in overlay marks this code for Section 232 steel review and possible AD/CVD follow-up.'],
  },
  {
    htsCode: '7306.61.5000',
    description: 'Other welded tubes of iron or nonalloy steel, square or rectangular',
    units: ['kg'],
    generalRate: '3.1%',
    flags: ['AD_CVD_POSSIBLE'],
    notes: ['Current built-in overlay marks this code as needing AD/CVD review.'],
  },
  {
    htsCode: '9903.17.01',
    description: 'Eligible to be imported under the first quota period specified in a notice issued by the United States Trade Representative',
    flags: ['QUOTA_OR_VISA'],
    notes: ['Derived from the USITC HTS description referencing a quota period.'],
  },
  {
    htsCode: '2204.21.30.00',
    description: 'Tokay wine designation under Internal Revenue Service rules',
    generalRate: '6.3¢/liter',
    specialRate: 'Free (A,AU,BH,CL,CO,D,E,IL,KR,MA,OM,P,PA,PE,S,SG) 2.8¢/liter(JO)',
    footnotesText: ['Imports under this subheading may be subject to Federal Excise Tax (26 U.S.C. 5041).'],
    flags: ['IRC_TAX_APPLICABLE', 'SPECIAL_PROGRAM_INDICATOR_ALLOWED'],
    notes: ['Derived from the USITC HTS footnote referencing federal excise tax.'],
  },
]

export const CURRENT_USITC_HTS_DATASET: UsitcHtsDatasetManifest = SNAPSHOT_PAYLOAD.manifest

function normalizeHtsCode(value: string) {
  const trimmed = value.trim().toUpperCase()
  if (!trimmed) return ''
  const digits = trimmed.replace(/[^0-9]/g, '')
  if (digits.length === 10) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 10)}`
  }
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}`
  }
  if (digits.length === 6) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}`
  }
  return trimmed
}

function uniqueFlags(flags: HtsRegulatoryFlag[]) {
  return [...new Set(flags)]
}

function uniqueStrings(values: Array<string | undefined | null>) {
  return [...new Set(values.filter((value): value is string => Boolean(value && value.trim())))]
}

function inferFlags(record: Omit<NormalizedHtsRecord, 'flags'> & { flags?: HtsRegulatoryFlag[] }) {
  const text = [
    record.description,
    record.generalRate,
    record.specialRate,
    record.otherRate,
    record.quotaQuantity,
    record.additionalDuties,
    ...(record.footnotesText ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  const inferred: HtsRegulatoryFlag[] = [...(record.flags ?? [])]

  if (text.includes('quota') || text.includes('visa')) inferred.push('QUOTA_OR_VISA')
  if (text.includes('excise tax') || text.includes('internal revenue')) inferred.push('IRC_TAX_APPLICABLE')
  if (text.includes('antidumping') || text.includes('countervailing') || text.includes('ad/cvd')) inferred.push('AD_CVD_POSSIBLE')
  if ((record.specialRate ?? '').trim()) inferred.push('SPECIAL_PROGRAM_INDICATOR_ALLOWED')

  return uniqueFlags(inferred)
}

function buildAncestorCodes(code: string, rawCode?: string) {
  const normalizedCode = normalizeHtsCode(code)
  if (!normalizedCode) return []

  const digits = normalizedCode.replace(/[^0-9]/g, '')
  if (digits.length === 10) {
    return uniqueStrings([
      normalizedCode,
      `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}`,
      `${digits.slice(0, 4)}.${digits.slice(4, 6)}`,
      digits.slice(0, 4),
    ])
  }

  const rawParts = String(rawCode ?? code)
    .trim()
    .split('.')
    .filter(Boolean)

  if (rawParts.length > 1) {
    const ancestors: string[] = []
    for (let index = rawParts.length; index > 0; index -= 1) {
      ancestors.push(normalizeHtsCode(rawParts.slice(0, index).join('.')))
    }
    return uniqueStrings(ancestors)
  }

  const parts = normalizedCode.split('.')
  const ancestors: string[] = []
  for (let index = parts.length; index > 0; index -= 1) {
    ancestors.push(parts.slice(0, index).join('.'))
  }
  return uniqueStrings(ancestors)
}

function firstDefined<T>(values: Array<T | undefined | null>) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

export class UsitcHtsLookupService {
  private readonly records = new Map<string, NormalizedHtsRecord>()
  private readonly manifest: UsitcHtsDatasetManifest

  constructor(
    seedRecords: NormalizedHtsRecord[] = SNAPSHOT_PAYLOAD.records,
    manifest: UsitcHtsDatasetManifest = SNAPSHOT_PAYLOAD.manifest,
  ) {
    this.manifest = manifest
    for (const record of seedRecords) {
      this.records.set(normalizeHtsCode(record.htsCode), {
        ...record,
        htsCode: normalizeHtsCode(record.htsCode),
        superior: record.superior ? normalizeHtsCode(record.superior) : null,
        flags: inferFlags(record),
      })
    }

    for (const record of BUILTIN_REGULATORY_OVERLAYS) {
      this.records.set(normalizeHtsCode(record.htsCode), {
        ...this.records.get(normalizeHtsCode(record.htsCode)),
        ...record,
        htsCode: normalizeHtsCode(record.htsCode),
        superior: record.superior ? normalizeHtsCode(record.superior) : null,
        flags: inferFlags({
          ...this.records.get(normalizeHtsCode(record.htsCode)),
          ...record,
        }),
      })
    }
  }

  getDatasetManifest() {
    return this.manifest
  }

  lookup(code: string) {
    const normalizedCode = normalizeHtsCode(code)
    if (!normalizedCode) return null

    const directRecord = this.records.get(normalizedCode) ?? null

    const lineage = buildAncestorCodes(normalizedCode, directRecord?.rawHtsCode)
      .map((candidate) => this.records.get(candidate))
      .filter((record): record is NormalizedHtsRecord => Boolean(record))

    if (lineage.length === 0) return null

    const exactRecord = lineage[0]

    return {
      ...exactRecord,
      htsCode: normalizedCode,
      description: firstDefined(lineage.map((record) => record.description)) ?? '',
      units: firstDefined(lineage.map((record) => (record.units?.length ? record.units : undefined))) ?? [],
      generalRate: firstDefined(lineage.map((record) => record.generalRate)) ?? '',
      specialRate: firstDefined(lineage.map((record) => record.specialRate)) ?? '',
      otherRate: firstDefined(lineage.map((record) => record.otherRate)) ?? '',
      quotaQuantity: firstDefined(lineage.map((record) => record.quotaQuantity)) ?? null,
      additionalDuties: firstDefined(lineage.map((record) => record.additionalDuties)) ?? undefined,
      footnotesText: uniqueStrings(lineage.flatMap((record) => record.footnotesText ?? [])),
      notes: uniqueStrings(lineage.flatMap((record) => record.notes ?? [])),
      flags: uniqueFlags(lineage.flatMap((record) => record.flags ?? [])),
    } satisfies NormalizedHtsRecord
  }

  registerRecords(records: NormalizedHtsRecord[]) {
    for (const record of records) {
      this.records.set(normalizeHtsCode(record.htsCode), {
        ...record,
        htsCode: normalizeHtsCode(record.htsCode),
        superior: record.superior ? normalizeHtsCode(record.superior) : null,
        flags: inferFlags(record),
      })
    }
  }
}

export const usitcHtsLookupService = new UsitcHtsLookupService()

export { normalizeHtsCode }
